import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { apiService } from '../services/api';
import {
  OperationsEquipment,
  OperationsEquipmentCreate,
  OperationsEquipmentUpdate,
} from '../types/api';

interface EquipmentState {
  equipment: OperationsEquipment[];
  loading: boolean;
  error: string | null;
  ongoingRequests: Set<string>;
}

interface EquipmentActions {
  loadEquipment: () => Promise<void>;
  createEquipment: (data: OperationsEquipmentCreate) => Promise<OperationsEquipment | null>;
  updateEquipment: (
    id: string,
    data: OperationsEquipmentUpdate
  ) => Promise<OperationsEquipment | null>;
  deleteEquipment: (id: string) => Promise<boolean>;
  reset: () => void;
}

type EquipmentStore = EquipmentState & EquipmentActions;

const initialState: EquipmentState = {
  equipment: [],
  loading: false,
  error: null,
  ongoingRequests: new Set<string>(),
};

export const useEquipmentStore = create<EquipmentStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      loadEquipment: async () => {
        const { ongoingRequests } = get();
        const key = 'load-equipment';
        if (ongoingRequests.has(key)) return;
        ongoingRequests.add(key);

        set({ loading: true, error: null }, false, 'loadEquipment-start');

        try {
          const equipment = await apiService.getOperationsEquipment();
          set({ equipment, loading: false }, false, 'loadEquipment-success');
        } catch (err) {
          const message = 'Failed to load operations equipment';
          console.error(message, err);
          set({ loading: false, error: message }, false, 'loadEquipment-error');
        } finally {
          ongoingRequests.delete(key);
        }
      },

      createEquipment: async (data) => {
        try {
          const item = await apiService.createOperationsEquipment(data);
          set((state) => ({ equipment: [...state.equipment, item] }), false, 'createEquipment');
          return item;
        } catch (err) {
          console.error('Failed to create equipment', err);
          set({ error: 'Failed to create equipment' }, false, 'createEquipment-error');
          return null;
        }
      },

      updateEquipment: async (id, data) => {
        try {
          const updated = await apiService.updateOperationsEquipment(id, data);
          set(
            (state) => ({
              equipment: state.equipment.map((e) => (e.id === id ? updated : e)),
            }),
            false,
            'updateEquipment'
          );
          return updated;
        } catch (err) {
          console.error('Failed to update equipment', err);
          set({ error: 'Failed to update equipment' }, false, 'updateEquipment-error');
          return null;
        }
      },

      deleteEquipment: async (id) => {
        try {
          await apiService.deleteOperationsEquipment(id);
          set(
            (state) => ({
              equipment: state.equipment.filter((e) => e.id !== id),
            }),
            false,
            'deleteEquipment'
          );
          return true;
        } catch (err) {
          console.error('Failed to delete equipment', err);
          set({ error: 'Failed to delete equipment' }, false, 'deleteEquipment-error');
          return false;
        }
      },

      reset: () => {
        set({ ...initialState, ongoingRequests: new Set() }, false, 'reset');
      },
    }),
    { name: 'equipment-store' }
  )
);
